import api from './api'

// Contact types
export interface Contact {
  id: string
  waContact: string
  name: string | null
  email: string | null
  phone: string | null
  company: string | null
  position: string | null
  notes: string | null
  customFields: Record<string, unknown> | null
  status: string
  isFavorite: boolean
  source: string
  lastContactAt: Date | null
  totalConversations: number
  tags: Array<{
    id: string
    name: string
    color: string
  }>
  createdAt: Date
  updatedAt: Date
}

export interface ContactDetail extends Contact {
  recentConversations: Array<{
    id: string
    status: string
    mode: string
    channelType: string
    createdAt: Date
    updatedAt: Date
  }>
}

export interface TimelineEvent {
  id: string
  type: 'message' | 'note' | 'conversation_status' | 'tag_added' | 'tag_removed' | 'mode_changed'
  timestamp: Date
  data: Record<string, unknown>
}

export interface ContactsFilters {
  search?: string
  tag?: string
  channel?: string
  status?: string
  isFavorite?: boolean
  recent?: boolean
  sortBy?: string
  sortDir?: 'asc' | 'desc'
  page?: number
  limit?: number
}

export interface ContactsResponse {
  data: Contact[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

export interface TimelineResponse {
  data: TimelineEvent[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

export interface UpdateContactRequest {
  name?: string
  email?: string
  phone?: string
  company?: string
  position?: string
  notes?: string
  customFields?: Record<string, unknown>
  source?: string
}

export interface CreateContactRequest {
  waContact: string
  name?: string
  email?: string
  phone?: string
  company?: string
  position?: string
  notes?: string
  customFields?: Record<string, unknown>
  source?: string
  /** Tag IDs to assign on creation */
  tagIds?: string[]
}

export const contactsApi = {
  // List contacts with filters
  async getContacts(filters: ContactsFilters = {}): Promise<ContactsResponse> {
    const params = new URLSearchParams()

    if (filters.search) params.append('search', filters.search)
    if (filters.tag) params.append('tag', filters.tag)
    if (filters.channel) params.append('channel', filters.channel)
    if (filters.status) params.append('status', filters.status)
    if (filters.isFavorite !== undefined) params.append('isFavorite', String(filters.isFavorite))
    if (filters.recent !== undefined) params.append('recent', String(filters.recent))
    if (filters.sortBy) params.append('sortBy', filters.sortBy)
    if (filters.sortDir) params.append('sortDir', filters.sortDir)
    if (filters.page) params.append('page', filters.page.toString())
    if (filters.limit) params.append('limit', filters.limit.toString())

    const response = await api.get(`/api/crm/contacts?${params.toString()}`)

    return {
      data: response.data.data.map((contact: any) => ({
        ...contact,
        lastContactAt: contact.lastContactAt ? new Date(contact.lastContactAt) : undefined,
        createdAt: new Date(contact.createdAt),
        updatedAt: new Date(contact.updatedAt),
      })),
      pagination: response.data.pagination,
    }
  },

  // Get contact details by ID
  async getContactById(contactId: string): Promise<ContactDetail> {
    const response = await api.get(`/api/crm/contacts/${contactId}`)

    return {
      ...response.data.data,
      lastContactAt: response.data.data.lastContactAt
        ? new Date(response.data.data.lastContactAt)
        : null,
      createdAt: new Date(response.data.data.createdAt),
      updatedAt: new Date(response.data.data.updatedAt),
      recentConversations: (response.data.data.recentConversations || []).map((conv: any) => ({
        ...conv,
        createdAt: new Date(conv.createdAt),
        updatedAt: new Date(conv.updatedAt),
      })),
    }
  },

  // Update contact
  async updateContact(contactId: string, data: UpdateContactRequest): Promise<Contact> {
    const response = await api.put(`/api/crm/contacts/${contactId}`, data)
    return {
      ...response.data.data,
      lastContactAt: response.data.data.lastContactAt
        ? new Date(response.data.data.lastContactAt)
        : null,
      createdAt: new Date(response.data.data.createdAt),
      updatedAt: new Date(response.data.data.updatedAt),
    }
  },

  // Toggle favorite
  async toggleFavorite(contactId: string): Promise<Contact> {
    const response = await api.patch(`/api/crm/contacts/${contactId}/favorite`)
    return {
      ...response.data.data,
      lastContactAt: response.data.data.lastContactAt
        ? new Date(response.data.data.lastContactAt)
        : null,
      createdAt: new Date(response.data.data.createdAt),
      updatedAt: new Date(response.data.data.updatedAt),
    }
  },

  // Update status
  async updateStatus(contactId: string, status: 'active' | 'inactive' | 'blocked'): Promise<Contact> {
    const response = await api.patch(`/api/crm/contacts/${contactId}/status`, { status })
    return {
      ...response.data.data,
      lastContactAt: response.data.data.lastContactAt
        ? new Date(response.data.data.lastContactAt)
        : null,
      createdAt: new Date(response.data.data.createdAt),
      updatedAt: new Date(response.data.data.updatedAt),
    }
  },

  // Get contact timeline
  async getContactTimeline(
    contactId: string,
    page = 1,
    limit = 50
  ): Promise<TimelineResponse> {
    const params = new URLSearchParams()
    params.append('page', page.toString())
    params.append('limit', limit.toString())

    const response = await api.get(`/api/crm/contacts/${contactId}/timeline?${params.toString()}`)

    return {
      data: response.data.data.map((event: any) => ({
        ...event,
        timestamp: new Date(event.timestamp),
      })),
      pagination: response.data.pagination,
    }
  },

  // Create a new contact manually
  async createContact(data: CreateContactRequest): Promise<Contact> {
    const response = await api.post('/api/crm/contacts', data)
    return {
      ...response.data.data,
      lastContactAt: response.data.data.lastContactAt
        ? new Date(response.data.data.lastContactAt)
        : null,
      createdAt: new Date(response.data.data.createdAt),
      updatedAt: new Date(response.data.data.updatedAt),
    }
  },

  // Delete a contact
  async deleteContact(contactId: string): Promise<void> {
    await api.delete(`/api/crm/contacts/${contactId}`)
  },

  // ==================== Contact Tags ====================

  // Add tag to contact
  async addTagToContact(contactId: string, tagId: string): Promise<void> {
    await api.post(`/api/crm/contacts/${contactId}/tags`, { tagId })
  },

  // Remove tag from contact
  async removeTagFromContact(contactId: string, tagId: string): Promise<void> {
    await api.delete(`/api/crm/contacts/${contactId}/tags/${tagId}`)
  },

  // Get contact tags
  async getContactTags(contactId: string): Promise<Array<{ id: string; name: string; color: string }>> {
    const response = await api.get(`/api/crm/contacts/${contactId}/tags`)
    return response.data.data
  },
}
